import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import api, { setAccessToken, clearAccessToken } from '../api/api';

const ProtectedRoute = ({ children }) => {
  const location = useLocation();
  const [authReady, setAuthReady] = useState(false);
  const [authToken, setAuthToken] = useState(localStorage.getItem('token'));
  const [checkingSetup, setCheckingSetup] = useState(true);
  const [redirectTo, setRedirectTo] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const ensureAuth = async () => {
      const existingToken = localStorage.getItem('token');
      if (existingToken) {
        if (!cancelled) {
          setAuthToken(existingToken);
          setAuthReady(true);
        }
        return;
      }

      try {
        const res = await api.post('/auth/refresh');
        const newToken = res?.data?.access_token;
        if (newToken && !cancelled) {
          setAccessToken(newToken);
          setAuthToken(newToken);
        }
      } catch (e) {
        // No refresh cookie or refresh failed; will redirect to login below
      } finally {
        if (!cancelled) {
          setAuthReady(true);
        }
      }
    };

    ensureAuth();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!authReady || !authToken) {
      return;
    }

    let cancelled = false;
    const intervalId = setInterval(async () => {
      try {
        const res = await api.post('/auth/refresh');
        const newToken = res?.data?.access_token;
        if (newToken && !cancelled) {
          setAccessToken(newToken);
          setAuthToken(newToken);
        }
      } catch {
        // If refresh fails, keep current token; user will be redirected on next 401
      }
    }, 30 * 60 * 1000); // every 30 minutes

    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [authReady, authToken]);

  useEffect(() => {
    if (!authReady || !authToken) {
      return;
    }

    let cancelled = false;
    const checkSetup = async () => {
      try {
        const res = await api.get('/user/setup-status');
        const data = res.data;
        if (cancelled) return;

        const hasProfessional = !!data.hasProfessional;
        const hasCalendar = !!data.hasCalendar;
        console.log('[ProtectedRoute] Setup status:', { hasProfessional, hasCalendar, pathname: location.pathname });

        // Enforce setup flow: professional -> calendar
        let shouldRedirect = null;

        // Allow staying on setup pages regardless of status
        const isOnSetupPage = location.pathname === '/professional-setup' || location.pathname === '/calendar-setup';

        if (!isOnSetupPage) {
          // Only redirect if NOT on a setup page
          if (!hasProfessional && location.pathname !== '/professional-setup') {
            // Must complete professional setup first
            shouldRedirect = '/professional-setup';
          } else if (hasProfessional && !hasCalendar && location.pathname !== '/calendar-setup') {
            // Professional done, must complete calendar setup
            shouldRedirect = '/calendar-setup';
          }
        }

        setRedirectTo(shouldRedirect);
      } catch (e) {
        console.error('[ProtectedRoute] Error checking setup:', e);
        if (e?.response?.status === 401) {
          clearAccessToken();
          setAuthToken(null);
        }
        setRedirectTo(null);
      } finally {
        if (!cancelled) {
          setCheckingSetup(false);
        }
      }
    };

    setCheckingSetup(true);
    checkSetup();

    return () => {
      cancelled = true;
    };
  }, [authReady, authToken, location.pathname]);

  // if (!authReady) {
  //   return <div style={{ textAlign: 'center', marginTop: '50px' }}>Loading...</div>;
  // }
  if (!authReady) {
   return null; // AppLayout PageLoader will handle UI
  }

  if (!authToken) {
    return <Navigate to="/login" replace />;
  }

  if (checkingSetup) {
    return null; // AppLayout PageLoader will handle UI
  }

  // if (checkingSetup) {
  //   return <div style={{ textAlign: 'center', marginTop: '50px' }}>Loading...</div>;
  // }

  if (redirectTo && location.pathname !== redirectTo) {
    return <Navigate to={redirectTo} replace />;
  }

  return children;
};

export default ProtectedRoute;
